<div class="request-details-page">
  <!-- Header with back navigation -->
  <header class="page-header">
    <div class="header-left">
      <ukg-button
        variant="tertiary"
        (click)="goBack()"
        (keydown.enter)="goBack()"
        class="back-btn">
        <span class="back-icon">&larr;</span>
        Back to Requests
      </ukg-button>
      @if (requestNumber) {
        <h1>Request Details - #{{ requestNumber }}</h1>
      }
    </div>

    <!-- Action Buttons -->
    <div class="action-buttons">
      <ng-container *appHasPermission="[Permissions.AccessToToolbar]">
        <ukg-button
          variant="primary"
          (click)="onUpdateRequest()"
          (keydown.enter)="onUpdateRequest()"
          class="action-btn">
          <ukg-icon name="save" class="btn-icon"></ukg-icon>
          Update Request
        </ukg-button>
        <ukg-button
          variant="secondary"
          [attr.disabled]="
            !canProcessRequest || (processRequestLoading$ | async) ? '' : null
          "
          (click)="onProcessRequest()"
          (keydown.enter)="onProcessRequest()"
          class="action-btn">
          <ukg-icon name="print" class="btn-icon"></ukg-icon>
          {{
            (processRequestLoading$ | async)
              ? 'Processing...'
              : 'Process Request'
          }}
        </ukg-button>
      </ng-container>
      <ukg-button
        variant="tertiary"
        [attr.disabled]="!canOpenShippingDetails ? '' : null"
        (click)="openShippingDetails()"
        (keydown.enter)="openShippingDetails()"
        class="action-btn">
        <ukg-icon name="truck" class="btn-icon"></ukg-icon>
        Shipping Details
      </ukg-button>
    </div>
  </header>

  <!-- Shipping Details Modal -->
  <app-shipping-details-modal
    [isOpen]="isShippingModalOpen"
    [requestId]="requestNumber"
    (closeModal)="closeShippingDetails()">
  </app-shipping-details-modal>

  <!-- Process Request Warning -->
  @if (processWarning) {
    <div class="warning-banner">
      <span class="warning-icon">!</span>
      <span>{{ processWarning }}</span>
      <button class="dismiss-btn" (click)="processWarning = null">
        &times;
      </button>
    </div>
  }

  <!-- Process Request Error -->
  @if (processRequestError$ | async; as processError) {
    <div class="error-banner">
      <span class="error-icon">!</span>
      <span>{{ processError }}</span>
    </div>
  }

  <!-- Success Message -->
  @if (successMessage) {
    <div class="success-banner">
      <span class="success-icon">&#10003;</span>
      <span>{{ successMessage }}</span>
      <button class="dismiss-btn" (click)="successMessage = null">
        &times;
      </button>
    </div>
  }

  <!-- Loading state -->
  @if (loading$ | async) {
    <div class="loading-container">
      <ukg-progress-circular></ukg-progress-circular>
      <p>Loading request details...</p>
    </div>
  }

  <!-- Error state -->
  @if (error$ | async; as error) {
    <div class="error-container">
      <div class="error-message">
        <span class="error-icon">!</span>
        <p>{{ error }}</p>
      </div>
      <button class="retry-btn" (click)="retry()">Retry</button>
      <button class="back-link" (click)="goBack()">
        Return to Requests List
      </button>
    </div>
  }

  <!-- Content -->
  @if (request$ | async; as request) {
    <!-- Metadata Section -->
    <section class="metadata-section">
      <h2>Request Information</h2>
      <form [formGroup]="detailForm" class="metadata-form">
        <!-- Request # -->
        <div class="form-field read-only field-request-number">
          <span class="field-label" id="requestNumberLabel">Request #</span>
          <span class="field-value" aria-labelledby="requestNumberLabel">
            {{ request.requestNumber }}
          </span>
        </div>
        <!-- Company Name -->
        <div class="form-field read-only field-company-name">
          <span class="field-label" id="companyNameLabel">Company Name</span>
          <span class="field-value" aria-labelledby="companyNameLabel">
            {{ displayValue(request.companyName) }}
          </span>
        </div>
        <!-- Contact Name -->
        <div class="form-field field-contact-name">
          <label for="contactName">Contact Name</label>
          <input type="text" id="contactName" formControlName="contactName" />
        </div>
        <!-- AR Number -->
        <div class="form-field read-only field-ar-number">
          <span class="field-label" id="arNumberLabel">AR Number</span>
          <span class="field-value" aria-labelledby="arNumberLabel">
            {{ displayValue(request.arNumber) }}
          </span>
        </div>
        <!-- Created By -->
        <div class="form-field read-only field-created-by">
          <span class="field-label" id="createdByLabel">Created By</span>
          <span class="field-value" aria-labelledby="createdByLabel">
            {{ displayValue(request.createdBy) }}
          </span>
        </div>
        <!-- Country -->
        <div class="form-field read-only field-country">
          <span class="field-label" id="countryLabel">Country</span>
          <span class="field-value" aria-labelledby="countryLabel">
            {{ displayValue(request.country) }}
          </span>
        </div>
        <!-- Phone Number - Editable -->
        <div
          class="form-field field-phone-number"
          *appHasPermission="[Permissions.AccessToEditAll]">
          <label for="phoneNumber">Phone Number</label>
          <input type="tel" id="phoneNumber" formControlName="phoneNumber" />
        </div>
        <!-- Phone Number - Read Only -->
        <div
          class="form-field read-only"
          *appHasPermission="[Permissions.AccessToEditAll]; inverse: true">
          <span class="field-label">Phone Number</span>
          <span class="field-value">
            {{ displayValue(request.phoneNumber) }}
          </span>
        </div>
        <!-- Email Address(es) - Editable -->
        <div
          class="form-field field-email-addresses"
          *appHasPermission="[Permissions.AccessToEditAll]">
          <label for="emailAddresses">Email Address(es)</label>
          <input
            type="text"
            id="emailAddresses"
            formControlName="emailAddresses" />
        </div>
        <!-- Email Address(es) - Read Only -->
        <div
          class="form-field read-only"
          *appHasPermission="[Permissions.AccessToEditAll]; inverse: true">
          <span class="field-label">Email Address(es)</span>
          <span class="field-value">
            {{ displayValue(request.emailAddresses) }}
          </span>
        </div>
        <ng-container
          *appHasPermission="
            [Permissions.AccessCustomersRequestsList];
            inverse: true
          ">
          <!-- Assigned To - Editable -->
          <div
            class="form-field field-assigned-to"
            *appHasPermission="[Permissions.AccessToEditAll]">
            <label for="assignedUserId">Assigned To</label>
            <select id="assignedUserId" formControlName="assignedUserId">
              <option value="">-- Unassigned --</option>
              @for (user of userListForDropdown$ | async; track user) {
                <option [value]="user.id">
                  {{ user.userName }}
                </option>
              }
            </select>
          </div>
          <!-- Assigned To - Read Only -->
          <ng-container
            *appHasPermission="[Permissions.AccessToEditAll]; inverse: true">
            <div
              class="form-field read-only"
              *appHasPermission="[Permissions.AccessToShowAssignedUser]">
              <span class="field-label">Assigned To</span>
              <span class="field-value">{{
                request.assignedTo || 'Unassigned'
              }}</span>
            </div>
          </ng-container>
        </ng-container>
        <!-- Request Status - Editable -->
        <div
          class="form-field field-request-status"
          *appHasPermission="[
            Permissions.AccessToEditAll,
            Permissions.AccessToChangeStatus,
          ]">
          <label for="requestStatusId">Request Status</label>
          <select id="requestStatusId" formControlName="requestStatusId">
            <option [ngValue]="null">Select Status</option>
            @for (status of requestStatusesForDropdown$ | async; track status) {
              <option [ngValue]="status.id">
                {{ status.status }}
              </option>
            }
          </select>
        </div>
        <!-- Request Status - Read Only -->
        <div
          class="form-field read-only"
          *appHasPermission="
            [Permissions.AccessToEditAll, Permissions.AccessToChangeStatus];
            inverse: true
          ">
          <span class="field-label">Request Status</span>
          @for (status of requestStatusesForDropdown$ | async; track status) {
            @if (status.id === request.requestStatusId) {
              <span class="field-value">
                {{ status.status }}
              </span>
            }
          }
        </div>
        <ng-container
          *appHasPermission="
            [Permissions.AccessCustomersRequestsList];
            inverse: true
          ">
          <ng-container
            *appHasPermission="[Permissions.AccessToPrintProcessFields]">
            <!-- Print Site (Dropdown) -->
            <div class="form-field field-print-site">
              <label for="printSiteId">Print Site</label>
              <select id="printSiteId" formControlName="printSiteId">
                <option value="">-- Select Print Site --</option>
                @for (site of printSitesForDropdown$ | async; track site) {
                  <option [value]="site.id">
                    {{ site.printSiteName }}
                  </option>
                }
              </select>
            </div>
            <!-- Print Location -->
            <div class="form-field field-print-location">
              <label for="printLocationId">Print Location</label>
              <div class="select-with-tooltip">
                <select
                  id="printLocationId"
                  formControlName="printLocationId"
                  [disabled]="!detailForm.get('printSiteId')?.value">
                  <option [ngValue]="null">Select Print Location</option>
                  @for (
                    location of printLocationsForDropdown$ | async;
                    track location
                  ) {
                    <option [ngValue]="location.id">
                      {{ location.locationName }}
                    </option>
                  }
                </select>
                <span class="tooltip-text"
                  >Location selection will only take effect upon processing this
                  request.</span
                >
              </div>
            </div>
            <!-- Print Postage -->
            <div class="form-field checkbox-field field-print-postage">
              <label for="printPostage">Print Postage</label>
              <div class="checkbox-container checkbox-with-tooltip">
                <input
                  type="checkbox"
                  id="printPostage"
                  formControlName="printPostage" />
                <span class="tooltip-text"
                  >Postage selection will only take effect upon processing this
                  request.</span
                >
              </div>
            </div>
          </ng-container>
        </ng-container>
        <!-- Expedited Request - Editable -->
        <div
          class="form-field radio-field field-expedited-request"
          *appHasPermission="[Permissions.AccessToEditAll]">
          <span class="field-label">Expedited Request</span>
          <div class="radio-container">
            <label class="radio-option">
              <input
                type="radio"
                [value]="true"
                formControlName="expeditedRequest" />
              <span>Yes</span>
            </label>
            <label class="radio-option">
              <input
                type="radio"
                [value]="false"
                formControlName="expeditedRequest" />
              <span>No</span>
            </label>
          </div>
        </div>
        <!-- Expedited Request - Read Only -->
        <div
          class="form-field read-only field-expedited-request"
          *appHasPermission="[Permissions.AccessToEditAll]; inverse: true">
          <span class="field-label">Expedited Request</span>
          <span class="field-value">
            {{ request.expeditedRequest ? 'Yes' : 'No' }}
          </span>
        </div>
        <!-- Waive Expedited Fee -->
        <div
          class="form-field radio-field field-waive-expedited-fee"
          *appHasPermission="[Permissions.AccessToWaiveSlaFee]">
          <span class="field-label">Waive Expedited Fee</span>
          <div class="radio-container">
            <label class="radio-option">
              <input
                type="radio"
                [value]="true"
                formControlName="waiveExpeditedFee" />
              <span>Yes</span>
            </label>
            <label class="radio-option">
              <input
                type="radio"
                [value]="false"
                formControlName="waiveExpeditedFee" />
              <span>No</span>
            </label>
          </div>
        </div>
        <!-- Special Handling -->
        <div
          class="form-field radio-field field-special-handling"
          *appHasPermission="[Permissions.AccessToSpecialHandling]">
          <span class="field-label">Special Handling</span>
          <div class="radio-container">
            <label class="radio-option">
              <input
                type="radio"
                [value]="true"
                formControlName="specialHandling" />
              <span>Yes</span>
            </label>
            <label class="radio-option">
              <input
                type="radio"
                [value]="false"
                formControlName="specialHandling" />
              <span>No</span>
            </label>
          </div>
        </div>
      </form>
    </section>
    <!-- Comments Section -->
    <section class="comments-section">
      <h2>Comments</h2>
      <form [formGroup]="detailForm" class="comments-container">
        <!-- New Comments Textarea -->
        <div class="comments-field">
          <label for="commentsField">New Comment:</label>
          <textarea
            id="commentsField"
            formControlName="comments"
            maxlength="4000"
            rows="5"></textarea>
          <small
            >{{ detailForm.get('comments')?.value?.length ?? 0 }} / 4000</small
          >
        </div>
        <!-- Comments History Textarea -->
        <div class="comments-field">
          <label for="commentsHistoryField">Comment History:</label>
          <textarea
            id="commentsHistoryField"
            formControlName="commentsHistory"
            readonly
            rows="5"></textarea>
        </div>
      </form>
    </section>
    <!-- File Details Grid -->
    <section class="file-grid-section">
      <h2>File Details</h2>
      <app-file-details-grid
        [fileDetails]="request.fileDetails"
        [canOpenFileDetails]="canOpenFileDetails"
        [showReadyToProcess]="showReadyToProcess"
        (fileRowClicked)="onFileRowClicked($event)"></app-file-details-grid>
    </section>
  }
</div>
